import React from 'react';
import PropTypes from 'prop-types';

import env from 'Env';
import imgixConfig from 'Config/imgix';
import useViewport from 'Hooks/useViewport';

const breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1520,
};

/**
 * Render a full width element with a mediaquery defined height (in px)
 * and a dynamic background image source passed through Imgix or Cloudflare
 * @param       {Object}  [heights]           Height of element for each breakpoint
 * @param       {String}  src                 Image file path
 * @param       {String}  [tagName='div']     Element tag name
 * @param       {String}  [className]         Class(es) to add to element
 * @param       {String}  [folder='product']  Image folder path
 * @param       {String}  [subfolder='']      Image subfolder path
 * @param       {Boolean} [fullQuality=false] Force higher quality image
 * @param       {Number}  [widthPercent=100]  Percentage width on the page
 * @param       {Number}  [widthOverride]     Pixel width on the page
 * @param       {String}  fit                 Imgix fit mode
 * @param       {Node}    children            Child elements
 * @param       {Node}    bgImgFluid          override for background fluid image class selector
 * @return      {Node}
 */
export default function FluidBackgroundImg({
  heights,
  src,
  tagName,
  className,
  folder,
  subfolder,
  fullQuality,
  widthPercent,
  widthOverride,
  fit,
  children,
  bgImgFluid,
}) {
  let breakpoint = 'xs';
  const { width: windowWidth } = useViewport();
  Object.keys(breakpoints).forEach((size) => {
    if (windowWidth >= breakpoints[size]) {
      breakpoint = size;
    }
  });

  const height = heights[breakpoint];
  const TagName = tagName;

  if (!height) {
    return (<TagName className={`${className}`} />);
  }

  // Rounded up to avoid too many different resizes
  const width = widthOverride || Math.ceil(windowWidth / 100) * widthPercent;

  const dpr = window.devicePixelRatio || 1;

  let subfolderString = subfolder;

  if (folder === 'product' && !subfolder) {
    subfolderString = 'original';
  }

  if (/\.svg$/.test(src)) {
    const root = env.srcCdnUrl || env.cdnUrl;
    const imgSrc = `${root}/${folder}${subfolderString ? '/' : ''}${subfolderString}/${src}`;
    const style = {
      backgroundImage: `url(${imgSrc})`,
    };

    return (
      <TagName className={`${bgImgFluid ? 'background-img-fluid' : ''} ${className}`} style={style}>
        {children}
      </TagName>
    );
  }

  const style = { backgroundPosition: 'center top', backgroundSize: 'cover' };

  const imgSrc = `${env.cdnUrl}/${folder}${subfolderString ? '/' : ''}${subfolderString}/${src}`;

  style.backgroundImage = `url(${imgSrc}?${imgixConfig}&dpr=${dpr}&fit=${fit}&h=${height}&w=${width}${fullQuality ? '&q=95' : ''})`;

  return (
    <TagName className={`${bgImgFluid ? 'background-img-fluid' : ''} ${className}`} style={style}>
      {children}
    </TagName>
  );
}

FluidBackgroundImg.propTypes = {
  heights: PropTypes.shape({
    xs: PropTypes.number,
    sm: PropTypes.number,
    md: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number,
    xxl: PropTypes.number,
  }),
  src: PropTypes.string.isRequired,
  tagName: PropTypes.string,
  fullQuality: PropTypes.bool,
  className: PropTypes.string,
  subfolder: PropTypes.string,
  folder: PropTypes.string,
  fit: PropTypes.string,
  widthPercent: PropTypes.number,
  widthOverride: PropTypes.number,
  children: PropTypes.node,
  bgImgFluid: PropTypes.bool,
};

FluidBackgroundImg.defaultProps = {
  heights: {
    xs: 200,
    sm: 200,
    md: 250,
    lg: 320,
    xl: 370,
    xxl: 450,
  },
  children: null,
  tagName: 'div',
  backgroundSize: 'auto',
  className: '',
  fullQuality: false,
  folder: 'product',
  widthPercent: 100,
  widthOverride: null,
  subfolder: '',
  fit: 'clip',
  bgImgFluid: true,
};
